import React, { memo, useCallback, useEffect, useState } from "react";
import {
  Button,
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  Spacer,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import colors from "app/colors";

import {
  CALLOUT_KEY,
  HASH_KEY,
  MONTH_DAY_YEAR_FORMAT,
} from "modules/utils/Constants";
import moment from "moment";
import {
  InfoIcon,
  PencilIcon,
  PeopleOutlineIcon,
  RestoreIcon,
  CircleMinusIcon,
} from "modules/icons";
import useBurstQuery from "modules/hooks/use-burst-query";
import InternalAdminDistributionGroupTooltip from "../components/internal-admin-distribution-group-tooltip";
import {
  InternalAdminTagProfileFragment,
  InternalAdminTagProfileQuery,
  InternalAdminTagProfileQueryVariables,
} from "gql/graphql";
import INTERNAL_ADMIN_TAG_PROFILE_QUERY from "../_networking/internal-admin-tag-profile";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";

interface IProps {
  tagId: string;
}

const InternalAdminTagDrawaer = ({ tagId }: IProps) => {
  const { openModal } = useOverlay();
  const [tag, setTag] = useState<InternalAdminTagProfileFragment | null>();

  const [internalAdminTagProfileAPI] = useBurstQuery<
    InternalAdminTagProfileQuery,
    InternalAdminTagProfileQueryVariables
  >(INTERNAL_ADMIN_TAG_PROFILE_QUERY);

  const getTag = useCallback(async () => {
    if (tagId) {
      const response = await internalAdminTagProfileAPI({
        variables: { id: tagId },
      });
      if (response.data?.internalAdminTag.length === 1) {
        setTag(response.data.internalAdminTag[0]);
      }
      return;
    }
    setTag(undefined);
  }, [tagId, internalAdminTagProfileAPI]);

  useEffect(() => {
    getTag();
  }, [getTag]);

  const onEditClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.INTERNAL_ADMIN_EDIT_TAG,
      id: tagId,
      onCloseCallback: getTag,
    });
  }, [getTag, openModal, tagId]);

  const onArchiveTagClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.INTERNAL_ADMIN_CONFIRM_ARCHIVE_TAG,
      ids: [tagId],
      onCloseCallback: getTag,
    });
  }, [getTag, openModal, tagId]);

  const onUnarchiveTagClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.INTERNAL_ADMIN_CONFIRM_UNARCHIVE_TAG,
      ids: [tagId],
      onCloseCallback: getTag,
    });
  }, [getTag, openModal, tagId]);

  return (
    <DrawerContent>
      <DrawerCloseButton right={0} left={1} />
      <DrawerHeader>
        <Center fontWeight="bold">Tag Details</Center>
        <Center height={10}>
          <Divider />
        </Center>
      </DrawerHeader>
      <DrawerBody>
        {!tag ? (
          <Spinner size="xl" />
        ) : (
          <VStack w="full" alignItems="flex-start">
            <HStack w="full" spacing={0}>
              <Text fontSize={28} fontWeight={700}>
                {HASH_KEY}
                {tag.displayName}
              </Text>
            </HStack>
            <HStack w="full">
              <InfoIcon boxSize={5} />
              <Text fontWeight={700}>Info</Text>
              <Spacer />
              <Button
                variant="ghost"
                color={colors.orangePrimary}
                onClick={onEditClicked}
                leftIcon={<PencilIcon fill={colors.orangePrimary} />}
              >
                Edit
              </Button>
            </HStack>
            <Divider borderColor={colors.black} />
            {tag.tagCategory && (
              <HStack w="full">
                <Text fontWeight={700} w={200}>
                  Category
                </Text>
                <Text>{tag.tagCategory.name}</Text>
              </HStack>
            )}
            <HStack w="full">
              <Text fontWeight={700} w={200}>
                Parent Tag
              </Text>
              <Text>{tag.parentTag ? tag.parentTag.name : "--"}</Text>
            </HStack>
            <HStack w="full">
              <Text fontWeight={700} w={200}>
                Created
              </Text>
              <Text>
                {moment(tag.createTime).format(MONTH_DAY_YEAR_FORMAT)}
              </Text>
            </HStack>
            {tag.active === false && tag.inactiveTime !== null && (
              <HStack w="full">
                <Text fontSize={14} fontWeight={600} w={200}>
                  Archived
                </Text>
                <Text fontSize={14} color={colors.gray50}>
                  {moment(tag.inactiveTime).format(MONTH_DAY_YEAR_FORMAT)}
                </Text>
              </HStack>
            )}
            <Divider />
            <HStack w="full">
              {tag.active ? (
                <Button
                  height={5}
                  p={0}
                  m={0}
                  leftIcon={
                    <CircleMinusIcon boxSize={5} fill={colors.gray40} />
                  }
                  onClick={onArchiveTagClicked}
                  variant="ghost"
                  color={colors.gray40}
                  w="fit-content"
                  alignSelf="flex-end"
                  fontSize={12}
                >
                  Archive Tag
                </Button>
              ) : (
                <Button
                  height={5}
                  p={0}
                  m={0}
                  leftIcon={<RestoreIcon boxSize={5} fill={colors.gray40} />}
                  onClick={onUnarchiveTagClicked}
                  variant="ghost"
                  color={colors.gray40}
                  w="fit-content"
                  alignSelf="flex-end"
                  fontSize={12}
                >
                  Unarchive Tag
                </Button>
              )}
            </HStack>{" "}
            {tag.distributionGroupList.length > 0 && (
              <>
                <HStack>
                  <PeopleOutlineIcon fill={colors.blackSecondary} boxSize={5} />
                  <Text fontWeight={700}>
                    Associated Groups ({tag.distributionGroupList.length})
                  </Text>
                </HStack>
                <Divider borderColor={colors.black} />
                <Text fontSize={11} color={colors.grayMain} pb={2}>
                  Groups that use this tag
                </Text>
                {tag.distributionGroupList.map((distributionGroup) => (
                  <React.Fragment key={distributionGroup.id}>
                    <InternalAdminDistributionGroupTooltip
                      distributionGroupId={distributionGroup.id}
                    >
                      <Text color={colors.blue} fontSize={12} fontWeight={700}>
                        {CALLOUT_KEY}
                        {distributionGroup.name}
                      </Text>
                    </InternalAdminDistributionGroupTooltip>
                    <Divider />
                  </React.Fragment>
                ))}
              </>
            )}
          </VStack>
        )}
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(InternalAdminTagDrawaer);
